import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { usePriceBreakdown } from 'src/components/molecules/PriceBreakdown/usePriceBreakdown';
import { selectHasOptions } from 'src/redux/basket/selectors';
import { actions as basketActions } from 'src/redux/basket/slice';
import { selectProduct } from 'src/redux/product/selectors';
import type { AppThunkDispatch } from 'src/redux/store';
import type { ReduxApplicationState } from 'src/redux/types';
import { isOutOfStock } from 'src/utils/inventoryStatus';
import type { ProductOptionsDispatchProps, ProductOptionsStateProps } from './ProductOptions';

type UseProductOptions = ProductOptionsDispatchProps & ProductOptionsStateProps;

export const useProductOptions = (): UseProductOptions => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const inStock = useSelector(
    (state: ReduxApplicationState) => !isOutOfStock(selectProduct(state).inventoryStatus),
  );
  const quantity = useSelector((state: ReduxApplicationState) => state.basket.quantity);
  const hasOptions = useSelector(selectHasOptions);
  // Called unconditionally, as the rules of hooks require. Whether the breakdown
  // is shown is decided here rather than inside the component, so the "does the
  // product have options" question lives in one place.
  const priceBreakdown = usePriceBreakdown();

  const onChangeQuantity = useCallback(
    ({ quantity, valid }: { quantity: number; valid: boolean }) => {
      dispatch(basketActions.changeQuantity({ isValid: valid, quantity }));
    },
    [dispatch],
  );

  return {
    inStock,
    onChangeQuantity,
    priceBreakdown: hasOptions ? priceBreakdown : null,
    quantity,
  };
};
